/**
 * Loot on the deck: crates, coins and health packs, drawn from the snapshot.
 *
 * The sim decides everything (see `@chaos/sim`'s loot.ts). This file only
 * draws the snapshot's `loot` block and dresses the transitions, and it keys
 * every transition off the SNAPSHOT DIFF, not off events: the server filters
 * `pickup` and `crate_broken` for an invisible player, but the crate still
 * leaves the public snapshot, so a diff-driven break still bursts on every
 * screen. Events only add the parts that name somebody — the toast, and whose
 * body the sparks rise off.
 *
 * Palette (the colour rule in styles.css): a coin is money, so it is gold. A
 * health pack is health going UP: a white medal with an --hp plus, a green beam
 * and a "+30 HP" tag, because a heal has to read as a heal before the name does.
 * A crate is ink steel banded in gold — it holds money, and is not money yet.
 */

import * as THREE from 'three/webgpu';
import { HEALTH_PACK_HEAL, COIN_DAMAGE_PCT, CRATE_HEALTH } from '@chaos/protocol';
import { CRATE_HALF_HEIGHT, CRATE_RADIUS } from '@chaos/sim';
import { createAccentMaterial, createPlayerToonMaterial, setFlashAmount } from '../ToonMaterials.js';
import { addHullOutline, removeHullOutline } from '../CheapOutline.js';
import { BAR_INK, makeSprite, setBarFill } from './HealthBars.js';
import { burst, burstStar, dust, requestLight, rise } from '../vfx/fx.js';
import { playBreakSound, playCollectSound, playHitSound } from '../audio/SoundManager.js';

// Mirrors of the styles.css tokens (--gold, --gold-hi, --hp, --hp-low, --ink).
// A WebGL colour cannot read a CSS variable; HealthBars mirrors the same ones.
const GOLD = 0xffc531;
const GOLD_HI = 0xffe27a;
const STEEL = 0x2b3a40;
const HP_GREEN = 0x00e07a;
const INK = 0x04080a;
/** The crate bar's fill at full health. The ink backing is a little wider. */
const CRATE_BAR_WIDTH = 1.08;
/** --event's cold white: stands off every deck, and is nobody's roster colour. */
const MEDAL_WHITE = 0xe8f2f4;
/** The health pack's light beam, tall enough to find from across the arena. */
const BEAM_HEIGHT = 7;

/** Pickups float this far over their floor, bob by BOB, and turn at SPIN rad/s. */
const HOVER = 0.75;
const BOB = 0.14;
const SPIN = 2.2;
/** New things pop in over this long, overshooting on the way. */
const POP_S = 0.32;
/** A health pack is dropped from this high, and lands in DROP_S. */
const DROP_FROM = 7;
const DROP_S = 0.45;
/** How long a toast is on screen: the `loot-toast` animation in styles.css. */
const TOAST_MS = 1300;
/** Crate wobble after a hit, decaying at this rate per second. */
const WOBBLE_DECAY = 7;

const _v = new THREE.Vector3();

// Geometry is shared by every instance; so are the pickup materials. Crates
// get their own materials because a hit flashes ONE crate, not all of them.
let _geo = null;
let _mats = null;

function geo() {
  if (_geo) return _geo;
  const w = CRATE_RADIUS * 1.9;
  const h = CRATE_HALF_HEIGHT * 2;
  _geo = {
    crateBody: new THREE.BoxGeometry(w, h, w),
    crateBand: new THREE.BoxGeometry(w * 1.04, h * 0.12, w * 1.04),
    // The camera looks DOWN, so the top face is most of what a player sees:
    // a small gold slot plate on steel reads as a safe, a gold lid as a brick.
    crateLid: new THREE.BoxGeometry(w * 0.5, h * 0.06, w * 0.24),
    crateDial: new THREE.CylinderGeometry(w * 0.2, w * 0.2, 0.08, 20),
    crateSlot: new THREE.BoxGeometry(w * 0.4, 0.05, 0.1),
    coin: new THREE.CylinderGeometry(0.42, 0.42, 0.13, 28),
    coinFace: new THREE.CylinderGeometry(0.28, 0.28, 0.14, 24),
    // The health pack: a medal with a plus that stands proud of BOTH faces, so
    // it reads as a plus from any angle of the spin.
    medal: new THREE.CylinderGeometry(0.62, 0.62, 0.16, 32),
    plusBar: new THREE.BoxGeometry(0.78, 0.24, 0.26),
    beam: new THREE.CylinderGeometry(0.38, 0.5, BEAM_HEIGHT, 20, 1, true),
    ring: new THREE.RingGeometry(0.55, 0.78, 32),
  };
  _geo.ring.rotateX(-Math.PI / 2);
  return _geo;
}

function mats() {
  if (_mats) return _mats;
  _mats = {
    gold: createPlayerToonMaterial(GOLD, { emissiveIntensity: 0.55 }),
    goldHi: createAccentMaterial(GOLD_HI),
    green: createPlayerToonMaterial(HP_GREEN, { emissiveIntensity: 0.6 }),
    ringGold: ringMaterial(GOLD),
    ringGreen: ringMaterial(HP_GREEN),
    medal: createPlayerToonMaterial(MEDAL_WHITE, { emissiveIntensity: 0.45 }),
    beam: beamMaterial(),
    healLabel: labelMaterial(),
  };
  return _mats;
}

function beamMaterial() {
  const m = new THREE.MeshBasicNodeMaterial({ color: HP_GREEN, transparent: true, opacity: 0.2 });
  m.depthWrite = false;
  m.side = THREE.DoubleSide;
  m.blending = THREE.AdditiveBlending;
  return m;
}

function labelMaterial() {
  const m = new THREE.SpriteNodeMaterial({ transparent: true });
  if (typeof document !== 'undefined') m.map = healLabelTexture();
  m.depthTest = false;
  m.depthWrite = false;
  return m;
}

function ringMaterial(color) {
  const m = new THREE.MeshBasicNodeMaterial({ color, transparent: true, opacity: 0.45 });
  m.depthWrite = false;
  return m;
}

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------

function buildCrate() {
  const g = geo();
  const steel = createPlayerToonMaterial(STEEL, { emissiveIntensity: 0.12 });
  const gold = createPlayerToonMaterial(GOLD, { emissiveIntensity: 0.45 });
  const root = new THREE.Group();
  const h = CRATE_HALF_HEIGHT * 2;

  const body = new THREE.Mesh(g.crateBody, steel);
  root.add(body);
  for (const y of [-h * 0.3, h * 0.3]) {
    const band = new THREE.Mesh(g.crateBand, gold);
    band.position.y = y;
    root.add(band);
  }
  const lid = new THREE.Mesh(g.crateLid, gold);
  lid.position.y = h / 2 + 0.03;
  root.add(lid);
  const slot = new THREE.Mesh(g.crateSlot, createAccentMaterial(INK));
  slot.position.y = h / 2 + 0.08;
  slot.userData.noOutline = true;
  root.add(slot);
  // The dial faces the camera, which sits on +Z at yaw 0.
  const dial = new THREE.Mesh(g.crateDial, gold);
  dial.rotation.x = Math.PI / 2;
  dial.position.z = CRATE_RADIUS * 0.96;
  root.add(dial);

  addHullOutline(root);

  // Its own little health bar: a value running down, so green to red like a
  // body's, and only once it has actually been hit.
  const barRoot = new THREE.Group();
  barRoot.position.y = h / 2 + 0.55;
  const back = makeSprite(BAR_INK, 1);
  back.scale.set(1.2, 0.2, 1);
  const fill = makeSprite(HP_GREEN, 1);
  fill.scale.set(CRATE_BAR_WIDTH, 0.11, 1);
  fill.renderOrder = 21;
  back.renderOrder = 20;
  barRoot.add(back, fill);
  barRoot.visible = false;
  root.add(barRoot);

  return { root, bar: { root: barRoot, fill }, materials: [steel, gold, slot.material, back.material, fill.material] };
}

/** A pickup's colour: green for health going up, gold for money. */
function colorOf(kind) {
  return kind === 'health_pack' ? HP_GREEN : GOLD;
}

/** How many sparks rise off the body that took a pickup. */
function sparksOf(kind) {
  return kind === 'health_pack' ? 12 : 9;
}

function buildCoin() {
  const g = geo();
  const m = mats();
  const root = new THREE.Group();
  const spinner = new THREE.Group();
  const coin = new THREE.Mesh(g.coin, m.gold);
  coin.rotation.x = Math.PI / 2;
  spinner.add(coin);
  const face = new THREE.Mesh(g.coinFace, m.goldHi);
  face.rotation.x = Math.PI / 2;
  face.userData.noOutline = true;
  spinner.add(face);
  addHullOutline(spinner);
  root.add(spinner);
  const ring = new THREE.Mesh(g.ring, m.ringGold);
  root.add(ring);
  return { root, spinner, ring };
}

/**
 * The health pack has to say "health" before anyone knows the word.
 *
 * The first cut was a green up-tick, and on a green-teal deck like The Chart
 * it disappeared into the floor — nobody could find it, and nobody who did
 * could tell it heals. Now it is the universal heal sign: a WHITE medal (white
 * stands off every deck in the game) carrying a big --hp plus, under a soft
 * green light beam you can find from across the arena, with "+30 HP" floating
 * over it so the name never has to explain itself.
 */
function buildHealthPack() {
  const g = geo();
  const m = mats();
  const root = new THREE.Group();
  const spinner = new THREE.Group();
  const medal = new THREE.Mesh(g.medal, m.medal);
  medal.rotation.x = Math.PI / 2;
  spinner.add(medal);
  const bar = new THREE.Mesh(g.plusBar, m.green);
  spinner.add(bar);
  const post = new THREE.Mesh(g.plusBar, m.green);
  post.rotation.z = Math.PI / 2;
  spinner.add(post);
  addHullOutline(spinner);
  root.add(spinner);

  const beam = new THREE.Mesh(g.beam, m.beam);
  beam.position.y = BEAM_HEIGHT / 2;
  beam.renderOrder = 8;
  root.add(beam);

  const label = new THREE.Sprite(m.healLabel);
  label.scale.set(1.5, 0.5, 1);
  label.position.y = HOVER + 1.25;
  label.renderOrder = 22;
  root.add(label);

  const ring = new THREE.Mesh(g.ring, m.ringGreen);
  ring.scale.setScalar(1.3);
  root.add(ring);
  return { root, spinner, ring, beam };
}

/** "+30 HP" on a green chip, drawn once and shared by every health pack. */
function healLabelTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 192;
  canvas.height = 64;
  const c = canvas.getContext('2d');
  c.fillStyle = '#04080a';
  c.beginPath();
  c.roundRect(2, 2, 188, 60, 30);
  c.fill();
  c.fillStyle = '#00e07a';
  c.beginPath();
  c.roundRect(7, 7, 178, 50, 25);
  c.fill();
  c.fillStyle = '#04080a';
  c.font = '900 36px "Lilita One", system-ui, sans-serif';
  c.textAlign = 'center';
  c.textBaseline = 'middle';
  c.fillText(`+${HEALTH_PACK_HEAL} HP`, 96, 34);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

// ---------------------------------------------------------------------------
// The renderer
// ---------------------------------------------------------------------------

export class LootRenderer {
  constructor(scene) {
    this.scene = scene;
    /** id -> { snap, model, born, wobble } */
    this.crates = new Map();
    /** id -> { snap, model, born, dropFrom } */
    this.pickups = new Map();
    /** pickupId -> event, for the toast and the taker's sparks. */
    this._taken = new Map();
    /** Health packs announced by `loot_spawn` fall from the sky. */
    this._dropping = new Set();
    /** Crate id -> when it left the snapshot. See `onHit`. */
    this._broken = new Map();
    this._toast = null;
    /** What the toast slot is showing, and until when. See `_showToast`. */
    this._toastPriority = 0;
    this._toastUntil = 0;
    /** Ids seen in the snapshot being folded in. Reused, not reallocated. */
    this._seenCrates = new Set();
    this._seenPickups = new Set();
  }

  /** Folds one snapshot's loot block in. Missing block means "no loot". */
  update(loot, now = performance.now()) {
    const crates = loot?.crates ?? [];
    const pickups = loot?.pickups ?? [];

    const seenV = this._seenCrates;
    seenV.clear();
    for (const v of crates) {
      seenV.add(v.id);
      let entry = this.crates.get(v.id);
      if (!entry) {
        const model = buildCrate();
        model.root.position.set(v.x, v.y, v.z);
        this.scene.add(model.root);
        entry = { snap: v, model, born: now, wobble: 0, flash: 0 };
        this.crates.set(v.id, entry);
      }
      entry.snap = v;
      const frac = v.health / CRATE_HEALTH;
      const bar = entry.model.bar;
      bar.root.visible = frac < 0.999;
      setBarFill(bar.fill, frac, CRATE_BAR_WIDTH);
    }
    for (const [id, entry] of this.crates) {
      if (seenV.has(id)) continue;
      this._breakCrate(entry);
      this.crates.delete(id);
      // The breaking `hit` arrives AFTER this snapshot (the room sends the
      // snapshot first), so remember the id long enough to recognise it.
      this._broken.set(id, now);
    }
    for (const [id, at] of this._broken) if (now - at > 2000) this._broken.delete(id);

    const seenP = this._seenPickups;
    seenP.clear();
    for (const p of pickups) {
      seenP.add(p.id);
      let entry = this.pickups.get(p.id);
      if (!entry) {
        const model = p.kind === 'health_pack' ? buildHealthPack() : buildCoin();
        model.root.position.set(p.x, p.y, p.z);
        this.scene.add(model.root);
        const dropFrom = this._dropping.delete(p.id) ? DROP_FROM : 0;
        // `dropFrom` > 0 while falling; zeroed on landing, which is also
        // what marks the landing as done.
        entry = { snap: p, model, born: now, dropFrom };
        this.pickups.set(p.id, entry);
      }
      entry.snap = p;
    }
    for (const [id, entry] of this.pickups) {
      if (seenP.has(id)) continue;
      this._removePickup(entry, this._taken.get(id));
      this._taken.delete(id);
      this.pickups.delete(id);
    }
  }

  /** `loot_spawn`: a health pack is declared — it falls, rather than appearing. */
  onSpawn(ev) {
    if (ev.kind !== 'health_pack') return;
    // The room sends the snapshot BEFORE the tick's events, so the pack is
    // usually already on the floor by now. Lift it and start the fall from
    // here; only mark it for `update` if it has not arrived yet.
    const entry = this.pickups.get(ev.pickupId);
    if (entry) {
      entry.born = performance.now();
      entry.dropFrom = DROP_FROM;
    } else {
      this._dropping.add(ev.pickupId);
    }
    // Say it out loud: a heal nobody knows about is a heal nobody fights for.
    this._showToast({ kind: 'declared' });
  }

  /**
   * `pickup`: who took it. The pickup leaves the snapshot on its own; this
   * only decides whose body the sparks rise off, and whether to toast.
   */
  onPickup(ev, selfId, takerPos) {
    const entry = this.pickups.get(ev.pickupId);
    if (!entry || (ev.kind === 'coin' && entry.snap.amount > ev.amount)) {
      // The room sends the snapshot BEFORE the events of the same tick, so the
      // pickup has usually left the scene already — and a partly-taken stack
      // never leaves it. Either way the removal path will not see this event,
      // so the taker's sparks go up here.
      if (takerPos) rise(takerPos, colorOf(ev.kind), sparksOf(ev.kind));
    } else {
      this._taken.set(ev.pickupId, { ...ev, takerPos: takerPos ? takerPos.clone() : null });
    }
    if (ev.playerId === selfId) {
      playCollectSound();
      this._showToast(ev);
    }
  }

  /** A `hit` whose target is a crate: knock it about and flash it. */
  onHit(targetId) {
    // The killing blow: the crate already burst off the snapshot.
    if (this._broken.has(targetId)) return true;
    const entry = this.crates.get(targetId);
    if (!entry) return false;
    entry.wobble = 1;
    entry.flash = 1;
    playHitSound(false);
    return true;
  }

  animate(dt, now) {
    const t = now / 1000;
    // One shared beam material, so one breath for every health pack on the deck.
    if (_mats) _mats.beam.opacity = 0.16 + 0.07 * Math.sin(t * 3.2);
    for (const entry of this.crates.values()) {
      const root = entry.model.root;
      const pop = popScale((now - entry.born) / 1000);
      entry.wobble = Math.max(0, entry.wobble - dt * WOBBLE_DECAY * entry.wobble - dt);
      const w = entry.wobble;
      root.rotation.z = Math.sin(t * 38) * 0.09 * w;
      root.rotation.x = Math.cos(t * 31) * 0.06 * w;
      root.scale.set(pop * (1 + 0.08 * w), pop * (1 - 0.1 * w), pop * (1 + 0.08 * w));
      if (entry.flash > 0) {
        entry.flash = Math.max(0, entry.flash - dt * 9);
        setFlashAmount(root, entry.flash);
      }
    }

    for (const entry of this.pickups.values()) {
      const { root, spinner, ring } = entry.model;
      const age = (now - entry.born) / 1000;
      const snap = entry.snap;
      let lift = HOVER + Math.sin(t * 2.6 + snap.x) * BOB;
      if (entry.dropFrom > 0) {
        const k = Math.min(1, age / DROP_S);
        lift += entry.dropFrom * (1 - k * k);
        if (k >= 1) {
          entry.dropFrom = 0;
          dust(snap.x, snap.y, snap.z, 8, 2.2);
          burstStar(_v.set(snap.x, snap.y + 0.4, snap.z), HP_GREEN, 1.4, 0.25);
        }
      }
      root.position.set(snap.x, snap.y, snap.z);
      spinner.position.y = lift;
      if (snap.kind === 'health_pack') {
        // Never edge-on: a spinning medal spends half its turn as a white
        // sliver. It faces the camera (which sits on +Z), leans back toward
        // the top-down view, and sways just enough to catch the light.
        spinner.rotation.x = -0.5;
        spinner.rotation.y = Math.sin(t * 1.8 + snap.x) * 0.45;
      } else {
        spinner.rotation.y = t * SPIN + snap.z;
      }
      // A stack reads bigger than a single coin, up to a point.
      const stack = snap.kind === 'coin' ? 1 + Math.min(4, snap.amount - 1) * 0.14 : 1.25;
      spinner.scale.setScalar(popScale(age) * stack);
      ring.scale.setScalar(1 + Math.sin(t * 3 + snap.x) * 0.06);
      requestLight(_v.set(snap.x, snap.y + lift, snap.z), colorOf(snap.kind), 3, 4);
    }
  }

  clear() {
    for (const entry of this.crates.values()) this._dispose(entry.model.root, entry.model.materials);
    for (const entry of this.pickups.values()) this._dispose(entry.model.root);
    this.crates.clear();
    this.pickups.clear();
    this._taken.clear();
    this._dropping.clear();
    this._broken.clear();
  }

  _breakCrate(entry) {
    const s = entry.snap;
    const at = _v.set(s.x, s.y, s.z);
    burst(at, GOLD, { count: 14, speed: 6 });
    burst(at, STEEL, { count: 10, speed: 4 });
    burstStar(at, GOLD_HI, 1.8, 0.28);
    dust(s.x, s.y - CRATE_HALF_HEIGHT, s.z, 10, 3);
    playBreakSound();
    this._dispose(entry.model.root, entry.model.materials);
  }

  _removePickup(entry, taken) {
    const s = entry.snap;
    const color = colorOf(s.kind);
    if (taken?.takerPos) {
      rise(taken.takerPos, color, sparksOf(s.kind));
    } else {
      burstStar(_v.set(s.x, s.y + HOVER, s.z), color, 1.1, 0.2);
    }
    this._dispose(entry.model.root);
  }

  _dispose(root, materials = []) {
    this.scene.remove(root);
    // The hulls own a material each and sit in CheapOutline's module-level
    // set; without this every lockbox and coin of the session outlives itself.
    removeHullOutline(root);
    for (const child of root.children) removeHullOutline(child);
    // Geometry and the shared pickup materials outlive any one instance; only
    // a crate's own materials go with it.
    for (const m of materials) m.dispose();
  }

  _showToast(ev) {
    const hud = document.getElementById('hud');
    if (!hud) return;
    // One slot, two kinds of news. What YOU just picked up outranks a spawn
    // announcement: a pack declared the moment you took one used to wipe the
    // "+30 HP" you had just earned (found by playing it).
    const now = performance.now();
    const priority = ev.kind === 'declared' ? 0 : 1;
    if (priority < this._toastPriority && now < this._toastUntil) return;
    this._toastPriority = priority;
    this._toastUntil = now + TOAST_MS;
    if (!this._toast) {
      this._toast = document.createElement('div');
      this._toast.className = 'loot-toast';
      hud.appendChild(this._toast);
    }
    const el = this._toast;
    if (ev.kind === 'declared') {
      el.dataset.kind = 'health_pack';
      el.textContent = `HEALTH IN THE CENTRE · +${HEALTH_PACK_HEAL} HP`;
    } else if (ev.kind === 'health_pack') {
      el.dataset.kind = 'health_pack';
      el.textContent = `HEALTH PACK · +${HEALTH_PACK_HEAL} HP`;
    } else {
      el.dataset.kind = 'coin';
      el.textContent = `+${ev.amount} COIN${ev.amount > 1 ? 'S' : ''} · +${COIN_DAMAGE_PCT * ev.amount}% DMG`;
    }
    // Restart the animation: remove, force a reflow, re-add.
    el.classList.remove('show');
    void el.offsetWidth;
    el.classList.add('show');
  }
}


/** 0 -> overshoot -> 1 over POP_S. */
function popScale(age) {
  if (age >= POP_S) return 1;
  const k = Math.max(0, age / POP_S);
  return Math.sin(k * Math.PI * 0.5) * (1 + 0.25 * Math.sin(k * Math.PI));
}
